import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";

import {
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";

import { IFieldArray } from "AngularFormly";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as LookupManagementServiceName } from "../../shared/configs/lookupManagement";
import { vsObjectName as projectCostTypeName } from "../../shared/configs/lookupManagement/projectCostType";
import { vsObjectName as projectCostCategoryName } from "../../shared/configs/lookupManagement/projectCostCategory";
import { StateService } from "@uirouter/angularjs";

export const projectConfigViewDefaultSort = "Id";
export const vsObjectName = "project";

projectConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];

export function projectConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  registerGrid(recordGridViewServiceProvider, columnDefinitionSetServiceProvider);
  registerForm(formViewServiceProvider);
}

export function getProjectColumnDefs(columnDefinitionService: ColumnDefinitionService) {
  return [
    columnDefinitionService.getNumberColDef({
      field: "Id",
      hide: true,
    }),
    columnDefinitionService.getStringColDef({
      field: "ProjectName",
      header: "Project Name",
    }),
    columnDefinitionService.getDateColDef({
      field: "StartDate",
      header: "Start Date",
    }),
    columnDefinitionService.getDateColDef({
      field: "EndDate",
      header: "End Date",
    }),
    columnDefinitionService.getNumberColDef({
      field: "SumTotal",
      header: "Sum Total",
    }),
    columnDefinitionService.getStringColDef({
      field: "Description",
      header: "Description",
    }),
  ];
}

function registerGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ColumnDefinitionSetServiceName,
    (columnDefinitionService: ColumnDefinitionService) =>
      getProjectColumnDefs(columnDefinitionService),
  ]);

  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    (serverUtility: ServerUtility, gridSettingsService: GridSettingsService) => {
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: projectConfigViewDefaultSort,
        gridDataSource: new RecordDataSource(serverUtility, "/api/project/Project"),
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        nameSingular: "Project",
        namePlural: "Projects",
        showAddButton: true,
        showAddButtonPermission: "projectCostEdit",
      };
    },
  ]);
}

function registerForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FormViewServiceName,
    FieldConfigurationHelperServiceName,
    LookupManagementServiceName,
    "$state",
    (
      formViewService: FormViewService,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      lookupManagementService: LookupManagementService,
      $state: StateService
    ) => {
      return {
        fields: <IFieldArray>[
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "Name",
                name: "Name",
                label: "Name ",
                required: true,
              }),
              fieldHelpers.simpleDateField({
                key: "StartDate",
                label: "Start Date",
              }),
              fieldHelpers.simpleDateField({
                key: "EndDate",
                label: "End Date",
              }),
            ],
            4
          ),
          fieldConfigurationHelperService.repeaterField({
            key: "ProjectCost",
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: "Project Cost",
            displayNamePlural: "Project Costs",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldConfigurationHelperService.lookupField(lookupManagementService, {
                    objectName: projectCostTypeName,
                    key: "ProjectCostTypeId",
                  }),
                fieldConfigurationHelperService.lookupField(lookupManagementService, {
                    objectName: projectCostCategoryName,
                    key: "ProjectCostCategoryId",
                    required: true,
                  }),
                fieldHelpers.simpleNumberField({
                  key: "Hours",
                  label: "Hours ",
                  required: true,
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "Rate",
                  label: "Rate",
                  watcher: [
                    {
                      listener: (field, value, _, scope) => {
                        field.value?.(value.Rate);
                      },
                      expression: (_, scope) => scope.model?.ProjectCostType,
                    },
                  ],
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-6",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                },
              ],
              4
            ),
            showHeader: false,
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: "Description",
                label: "Description ",
                rows: 5,
              }),
            ],
            1
          ),
          fieldHelpers.headerField({
            headerText: "Files",
            key: "FileHeader",
          }),
          {
            key: "ProjectFile",
            type: "recordFileUpload",
            templateOptions: {
              label: "Files",
              allowMultiple: true,
              hideLabel: true,
            },
          },
        ],
        name: vsObjectName,
        submitForm: (args) =>
          formViewService
            .saveForm({
              formObject: args.model,
              formViewController: args.formViewController,
              url: "/api/project/project",
            })
            .then(() => $state.go("main.projectCost.grid")),
        title: () => "Project",
        showButtonsAtTop: true,
      };
    },
  ]);
}
